const MAX_COUNT = 1000000;
const MAX_MS = 86400000;

const boundedCount = (value, fallback = 0) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(0, Math.min(MAX_COUNT, Math.floor(number)));
};

const boundedMs = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.max(0, Math.min(MAX_MS, Math.round(number)));
};

const finiteTimestamp = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

const interactionAuthority = (event = {}, { requireVisible = true } = {}) => {
  const numericFields = [
    event.interactionOrdinal,
    event.seriesLoadGeneration,
    event.viewportInstanceEpoch,
  ].map(Number);
  if (requireVisible) numericFields.push(Number(event.visibleRequestGeneration));
  if (!numericFields.every(value => Number.isSafeInteger(value) && value >= 0)) return null;
  const cacheEpoch = event.cacheEpoch;
  if (typeof cacheEpoch !== 'string' || !/^[0-9a-f]{32}$/.test(cacheEpoch)) return null;
  return JSON.stringify([...numericFields, cacheEpoch]);
};

export const createStudyInteractionPerformanceTracker = () => {
  const panes = new Map();
  const totals = {
    wheel_input_count: 0,
    visible_target_count: 0,
    accepted_visible_count: 0,
    unaccepted_target_count: 0,
    coalesced_skip_slice_count: 0,
    accepted_gap_slice_count: 0,
    decoded_cache_hit_count: 0,
    decoded_cache_miss_count: 0,
    accept_latency_sample_count: 0,
    accept_latency_total_ms: 0,
    accept_latency_max_ms: 0,
  };
  let revision = 0;
  let finalized = false;

  const increment = (field, amount = 1) => {
    totals[field] = Math.min(MAX_COUNT, totals[field] + boundedCount(amount));
  };
  const mutate = () => { revision = Math.min(MAX_COUNT, revision + 1); };
  const paneState = viewportIndex => {
    const key = boundedCount(viewportIndex);
    if (!panes.has(key)) panes.set(key, {
      input: null,
      pending: null,
      lastAcceptedSlice: null,
      lastAcceptedSeriesLoadGeneration: null,
      lastAcceptedViewportInstanceEpoch: null,
    });
    return panes.get(key);
  };
  const terminatePending = pane => {
    if (!pane.pending) return false;
    increment('unaccepted_target_count');
    pane.pending = null;
    return true;
  };

  const recordInput = (event = {}) => {
    if (finalized) return false;
    const authority = interactionAuthority(event, { requireVisible: false });
    const inputAtMs = finiteTimestamp(event.inputAtMs ?? event.atMs);
    if (!authority || inputAtMs == null) return false;
    const pane = paneState(event.viewportIndex);
    pane.input = { authority, inputAtMs };
    increment('wheel_input_count');
    increment('coalesced_skip_slice_count', Math.max(0, boundedCount(event.frameCount, 1) - 1));
    mutate();
    return true;
  };

  const recordTarget = (event = {}) => {
    if (finalized || !Number.isFinite(Number(event.targetSlice))) return false;
    const authority = interactionAuthority(event);
    const inputAuthority = interactionAuthority(event, { requireVisible: false });
    const pane = paneState(event.viewportIndex);
    if (!authority || !inputAuthority || !pane.input || pane.input.authority !== inputAuthority) return false;
    terminatePending(pane);
    pane.pending = {
      authority,
      targetSlice: Math.floor(Number(event.targetSlice)),
      startedAtMs: pane.input.inputAtMs,
      cacheRecorded: false,
    };
    pane.input = null;
    increment('visible_target_count');
    mutate();
    return true;
  };

  const recordCache = (event = {}) => {
    if (finalized) return false;
    const pane = paneState(event.viewportIndex);
    const authority = interactionAuthority(event);
    if (!authority || !pane.pending || pane.pending.authority !== authority || pane.pending.cacheRecorded) return false;
    pane.pending.cacheRecorded = true;
    increment(event.hit ? 'decoded_cache_hit_count' : 'decoded_cache_miss_count');
    mutate();
    return true;
  };

  const recordAccepted = (event = {}) => {
    if (finalized || !Number.isFinite(Number(event.slice))) return false;
    const pane = paneState(event.viewportIndex);
    const authority = interactionAuthority(event);
    const acceptedSlice = Math.floor(Number(event.slice));
    if (!authority || !pane.pending || pane.pending.authority !== authority
      || pane.pending.targetSlice !== acceptedSlice) return false;
    const acceptedAtMs = finiteTimestamp(event.atMs);
    if (acceptedAtMs == null) return false;
    const latency = boundedMs(Math.max(0, acceptedAtMs - pane.pending.startedAtMs));
    increment('accepted_visible_count');
    increment('accept_latency_sample_count');
    totals.accept_latency_total_ms = Math.min(
      MAX_MS * MAX_COUNT,
      totals.accept_latency_total_ms + latency,
    );
    totals.accept_latency_max_ms = Math.max(totals.accept_latency_max_ms, latency);
    const acceptedSeriesLoadGeneration = Number(event.seriesLoadGeneration);
    const acceptedViewportInstanceEpoch = Number(event.viewportInstanceEpoch);
    if (
      pane.lastAcceptedViewportInstanceEpoch === acceptedViewportInstanceEpoch
      && pane.lastAcceptedSeriesLoadGeneration === acceptedSeriesLoadGeneration
      && pane.lastAcceptedSlice != null
    ) {
      increment('accepted_gap_slice_count', Math.max(0, Math.abs(acceptedSlice - pane.lastAcceptedSlice) - 1));
    }
    pane.lastAcceptedSlice = acceptedSlice;
    pane.lastAcceptedSeriesLoadGeneration = acceptedSeriesLoadGeneration;
    pane.lastAcceptedViewportInstanceEpoch = acceptedViewportInstanceEpoch;
    pane.pending = null;
    mutate();
    return true;
  };

  const settle = ({ final = false } = {}) => {
    let changed = false;
    panes.forEach(pane => { changed = terminatePending(pane) || changed; });
    if (final && !finalized) {
      finalized = true;
      changed = true;
    }
    if (changed) mutate();
  };

  const snapshot = () => ({
    revision,
    finalized,
    ...totals,
  });

  const payload = ({ clientRunId } = {}) => ({
    schema_version: 1,
    client_run_id: clientRunId,
    ...snapshot(),
  });

  return {
    recordInput,
    recordTarget,
    recordCache,
    recordAccepted,
    settle,
    snapshot,
    payload,
    isDirty: () => revision > 0,
    isFinalized: () => finalized,
  };
};
